import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { SiteSettingsProvider } from "@/context/SiteSettingsContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrelaunchBanner from "@/components/PrelaunchBanner";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import { Login, Register } from "@/pages/Auth";
import Contact, { PolicyPlaceholder } from "@/pages/Contact";
import Orders from "@/pages/Orders";
import Admin from "@/pages/Admin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminSuppliers from "@/pages/AdminSuppliers";
import AdminSupplierProducts from "@/pages/AdminSupplierProducts";
import AdminPricing from "@/pages/AdminPricing";
import AdminExceptions from "@/pages/AdminExceptions";
import AdminWaitlist from "@/pages/AdminWaitlist";
import AdminEmails from "@/pages/AdminEmails";

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="container-tight py-24 font-mono text-zinc-500">LOADING...</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteSettingsProvider>
        <CartProvider>
          <div className="min-h-screen flex flex-col bg-[#050505]">
            <Header />
            <PrelaunchBanner />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/catalog" element={<Catalog />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Protected><Checkout /></Protected>} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/policies/shipping" element={<PolicyPlaceholder title="Shipping Policy" />} />
                <Route path="/policies/returns" element={<PolicyPlaceholder title="Return Policy" />} />
                <Route path="/policies/privacy" element={<PolicyPlaceholder title="Privacy Policy" />} />
                <Route path="/policies/terms" element={<PolicyPlaceholder title="Terms and Conditions" />} />
                <Route path="/account/orders" element={<Protected><Orders /></Protected>} />
                <Route path="/admin" element={<Protected admin><AdminDashboard /></Protected>} />
                <Route path="/admin/orders" element={<Protected admin><Admin /></Protected>} />
                <Route path="/admin/exceptions" element={<Protected admin><AdminExceptions /></Protected>} />
                <Route path="/admin/suppliers" element={<Protected admin><AdminSuppliers /></Protected>} />
                <Route path="/admin/supplier-products" element={<Protected admin><AdminSupplierProducts /></Protected>} />
                <Route path="/admin/pricing" element={<Protected admin><AdminPricing /></Protected>} />
                <Route path="/admin/waitlist" element={<Protected admin><AdminWaitlist /></Protected>} />
                <Route path="/admin/emails" element={<Protected admin><AdminEmails /></Protected>} />
              </Routes>
            </main>
            <Footer />
          </div>
          <Toaster theme="dark" position="top-right" />
        </CartProvider>
        </SiteSettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
